import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import type { ClinicalEpisode, ClinicalEvent, EventInput, EventActionInput } from './types';
export const flowsheetUrl = (orgId: string) => `/api/v1/organizations/${orgId}/flowsheets`;
export function useClinicalEpisodes(pregnancyId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery({
    queryKey: ['clinical-episodes', orgId, pregnancyId],
    enabled: !!orgId && !!pregnancyId,
    queryFn: async () =>
      (
        await clientApi.get<ClinicalEpisode[]>(`${flowsheetUrl(orgId!)}/episodes`, {
          params: { pregnancy_id: pregnancyId },
        })
      ).data,
  });
}
export function useStartEpisode(pregnancyId: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await clientApi.post<ClinicalEpisode>(`${flowsheetUrl(orgId!)}/episodes`, { pregnancy_id: pregnancyId })).data,
    onSuccess: () => cache.invalidateQueries({ queryKey: ['clinical-episodes', orgId, pregnancyId] }),
  });
}
export async function fetchAllEvents(orgId: string, episodeId: string): Promise<ClinicalEvent[]> {
  let after = 0;
  const events: ClinicalEvent[] = [];
  for (;;) {
    const { data } = await clientApi.get<{ events: ClinicalEvent[]; next_cursor: number; has_more: boolean }>(
      `${flowsheetUrl(orgId)}/episodes/${episodeId}/events`,
      { params: { after, limit: 500 } },
    );
    events.push(...data.events);
    if (!data.has_more) return events;
    if (data.next_cursor <= after) throw new Error('Invalid event feed cursor');
    after = data.next_cursor;
  }
}
export function useClinicalEvents(episodeId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery({
    queryKey: ['clinical-events', orgId, episodeId],
    enabled: !!orgId && !!episodeId,
    queryFn: () => fetchAllEvents(orgId!, episodeId!),
  });
}
export function useEventActions(episodeId: string) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const cache = useQueryClient();
  return useMutation({
    mutationFn: async ({ rootId, input }: { rootId: string; input: EventActionInput }) =>
      (
        await clientApi.post<ClinicalEvent>(
          `${flowsheetUrl(orgId!)}/episodes/${episodeId}/events/${rootId}/actions`,
          input,
        )
      ).data,
    onSuccess: () => cache.invalidateQueries({ queryKey: ['clinical-events', orgId, episodeId] }),
  });
}
export async function postEvent(orgId: string, episodeId: string, input: EventInput) {
  return (await clientApi.post<ClinicalEvent>(`${flowsheetUrl(orgId)}/episodes/${episodeId}/events`, input)).data;
}
export async function postEventBatch(orgId: string, episodeId: string, inputs: EventInput[]) {
  return (await clientApi.post<ClinicalEvent[]>(`${flowsheetUrl(orgId)}/episodes/${episodeId}/events/batch`, inputs))
    .data;
}

export function usePracticeConfiguration() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery({
    queryKey: ['flowsheet-configuration', orgId],
    enabled: !!orgId,
    queryFn: async () =>
      (await clientApi.get<import('./rules').PracticeConfiguration>(`${flowsheetUrl(orgId!)}/configuration`)).data,
  });
}
